import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import ConfirmDialog from "app/shared/widgets/ConfirmDialog";
import releaseNoteServices from "app/services/release-note-services";
import ReleaseNoteFormDialog from "./ReleaseNoteFormDialog";

const ReleaseNotesIndex = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();

    const [listLoading, setListLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const profileNames = useMemo(
        () => authUser?.profiles?.map((profile) => profile.name) || [],
        [authUser],
    );
    // Publicar novedades es de superadmin, como WhatsApp Inbox y Reportes de la app.
    const canViewModule = useMemo(() => profileNames.includes("superadmin"), [profileNames]);

    useEffect(() => {
        if (authUser && !canViewModule) {
            navigate("/trips");
        }
    }, [authUser, canViewModule, navigate]);

    const loadReleaseNotes = useCallback(() => {
        releaseNoteServices.getReleaseNotes("?include_drafts=1")
            .then((response) => setRecords(response.data || []))
            .catch((error) => {
                if (error?.response?.status === 403) navigate("/trips");
            })
            .finally(() => setListLoading(false));
    }, [navigate]);

    useEffect(() => { setListLoading(true); }, []);

    useEffect(() => {
        if (listLoading) loadReleaseNotes();
    }, [listLoading, loadReleaseNotes]);

    const borradores = useMemo(() => records.filter((r) => r.draft).length, [records]);

    const ejecutarAccion = async () => {
        if (!confirmAction) return;
        const { kind, record } = confirmAction;

        try {
            setActionLoading(true);
            if (kind === "publish") {
                await releaseNoteServices.publishReleaseNote(record.id);
                enqueueSnackbar("Novedades publicadas", { variant: "success" });
            } else if (kind === "unpublish") {
                await releaseNoteServices.unpublishReleaseNote(record.id);
                enqueueSnackbar("Novedades despublicadas", { variant: "success" });
            } else if (kind === "delete") {
                await releaseNoteServices.deleteReleaseNote(record.id);
                enqueueSnackbar("Novedades eliminadas", { variant: "success" });
            }
            setConfirmAction(null);
            setListLoading(true);
        } catch (_error) {
            // El interceptor global ya muestra el mensaje.
        } finally {
            setActionLoading(false);
        }
    };

    const textoConfirmacion = () => {
        if (!confirmAction) return {};
        const { kind, record } = confirmAction;
        const fecha = record?.released_on;

        if (kind === "publish") {
            return {
                title: "Publicar novedades",
                message: <>Las novedades del <strong>{fecha}</strong> van a quedar visibles en la app. ¿Publicamos?</>,
                confirmText: "Publicar",
            };
        }
        if (kind === "unpublish") {
            return {
                title: "Despublicar novedades",
                message: <>Las novedades del <strong>{fecha}</strong> dejan de verse en la app y vuelven a borrador. No se borra nada.</>,
                confirmText: "Despublicar",
            };
        }
        return {
            title: "Eliminar novedades",
            message: <>Se eliminan las novedades del <strong>{fecha}</strong>. Esto no se puede deshacer.</>,
            confirmText: "Eliminar",
        };
    };

    const renderTable = () => {
        if (listLoading) {
            return (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress color="warning" />
                </Stack>
            );
        }

        if (!records.length) {
            return (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary">
                            Todavía no hay novedades publicadas. Crea la primera tanda con el botón de arriba.
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, width: "100%" }}>
                <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "12%" }}>Fecha</TableCell>
                            <TableCell sx={{ width: "11%" }}>Estado</TableCell>
                            <TableCell sx={{ width: "10%" }}>Desde</TableCell>
                            <TableCell sx={{ width: "38%" }}>Cambios</TableCell>
                            <TableCell sx={{ width: "14%" }}>Autor</TableCell>
                            <TableCell align="right" sx={{ width: "15%" }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record) => (
                            <TableRow key={record.id} hover>
                                <TableCell>{moment(record.released_on).format("DD/MM/YYYY")}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={record.draft ? "Borrador" : "Publicada"}
                                        color={record.draft ? "default" : "success"}
                                    />
                                    {record.audience && record.audience !== "both" ? (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            sx={{ ml: 0.5 }}
                                            label={record.audience === "web" ? "Solo web" : "Solo app"}
                                        />
                                    ) : null}
                                </TableCell>
                                <TableCell>
                                    {record.min_app_version ? (
                                        <Tooltip title={`Solo la ve quien tenga la app ${record.min_app_version} o posterior`}>
                                            <Chip size="small" variant="outlined" label={`v${record.min_app_version}`} />
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">todas</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0.25}>
                                        {(record.items || []).slice(0, 2).map((linea, index) => (
                                            <Typography key={`${record.id}-${index}`} variant="body2" noWrap>
                                                · {linea}
                                            </Typography>
                                        ))}
                                        {(record.items || []).length > 2 && (
                                            <Typography variant="body2" color="text.secondary">
                                                y {record.items.length - 2} más
                                            </Typography>
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" noWrap>{record.author_name || "-"}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                        <Tooltip title="Editar">
                                            <IconButton size="small" color="primary"
                                                onClick={() => { setEditRecord(record); setShowForm(true); }}>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {record.draft ? (
                                            <Tooltip title="Publicar">
                                                <IconButton size="small" color="success"
                                                    onClick={() => setConfirmAction({ kind: "publish", record })}>
                                                    <PublishOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Despublicar">
                                                <IconButton size="small" color="warning"
                                                    onClick={() => setConfirmAction({ kind: "unpublish", record })}>
                                                    <UnpublishedOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Eliminar">
                                            <IconButton size="small" color="error"
                                                onClick={() => setConfirmAction({ kind: "delete", record })}>
                                                <DeleteOutlineOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (authUser && !canViewModule) return null;

    return (
        <React.Fragment>
            <ReleaseNoteFormDialog
                open={showForm}
                record={editRecord}
                onClose={() => { setShowForm(false); setEditRecord(null); }}
                onSaved={() => setListLoading(true)}
            />

            <ConfirmDialog
                open={Boolean(confirmAction)}
                loading={actionLoading}
                onClose={() => setConfirmAction(null)}
                onConfirm={ejecutarAccion}
                {...textoConfirmacion()}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="h2">Novedades</Typography>
                    {borradores > 0 && (
                        <Chip size="small" label={`${borradores} sin publicar`} />
                    )}
                </Stack>

                <Button
                    startIcon={<ControlPointOutlinedIcon />}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => { setEditRecord(null); setShowForm(true); }}
                >
                    Nueva tanda
                </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Lo que se publica acá es lo que el chofer ve en "Novedades" dentro de la app. Cada tanda
                solo le aparece a quien ya tenga una versión de esa fecha o posterior.
            </Typography>

            {renderTable()}
        </React.Fragment>
    );
};

export default ReleaseNotesIndex;
